import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CreditState, CreditBalance, CreditUsage, CreditTransaction, CreditPlan } from '@/types/credits'

interface CreditStore extends CreditState {
  // Actions
  updateBalance: (balance: CreditBalance) => void
  updateUsage: (usage: CreditUsage) => void
  addTransaction: (transaction: CreditTransaction) => void
  setPlans: (plans: CreditPlan[]) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  
  // Computed
  getUsagePercentage: () => number
  getRemainingCredits: () => number
  canSendMessage: () => boolean
  
  // API Actions (for future implementation)
  fetchBalance: () => Promise<void>
  fetchUsage: () => Promise<void>
  fetchTransactions: () => Promise<void>
  purchaseCredits: (planId: string) => Promise<void>
}

// Dummy data for initial state
const initialBalance: CreditBalance = {
  current: 2847,
  total: 5000,
  lastUpdated: new Date().toISOString()
}

const initialUsage: CreditUsage = {
  messagesUsed: 2153,
  campaignsUsed: 45,
  broadcastsUsed: 23,
  totalSpent: 2153,
  dailyUsage: []
}

const initialTransactions: CreditTransaction[] = [
  {
    id: '1',
    type: 'purchase',
    amount: 5000,
    description: 'Monthly Plan Purchase',
    timestamp: new Date(Date.now() - 86400000 * 5).toISOString()
  },
  {
    id: '2', 
    type: 'usage',
    amount: -450,
    description: 'WhatsApp Campaign Messages',
    timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
    channelId: 'whatsapp'
  },
  {
    id: '3',
    type: 'usage', 
    amount: -200,
    description: 'Telegram Broadcast',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    channelId: 'telegram'
  }
]

const initialPlans: CreditPlan[] = [
  {
    id: 'basic',
    name: 'Basic Plan',
    credits: 1000,
    price: 29,
    duration: 30,
    isActive: false,
    features: ['1,000 Messages', 'Basic Analytics', 'Email Support']
  },
  {
    id: 'pro',
    name: 'Pro Plan', 
    credits: 5000,
    price: 99,
    duration: 30,
    isActive: true,
    features: ['5,000 Messages', 'Advanced Analytics', 'Priority Support', 'API Access']
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    credits: 20000,
    price: 299,
    duration: 30,
    isActive: false,
    features: ['20,000 Messages', 'Custom Analytics', '24/7 Support', 'White Label']
  }
]

export const useCreditStore = create<CreditStore>()(
  persist(
    (set, get) => ({
      // State
      balance: initialBalance,
      usage: initialUsage,
      transactions: initialTransactions,
      availablePlans: initialPlans,
      isLoading: false,
      error: null,

      // Actions
      updateBalance: (balance) => set({ balance }),
      
      updateUsage: (usage) => set({ usage }),
      
      addTransaction: (transaction) => set((state) => ({
        transactions: [transaction, ...state.transactions]
      })),
      
      setPlans: (plans) => set({ availablePlans: plans }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      setError: (error) => set({ error }),

      // Computed
      getUsagePercentage: () => {
        const { balance } = get()
        return ((balance.total - balance.current) / balance.total) * 100
      },
      
      getRemainingCredits: () => {
        const { balance } = get()
        return balance.current
      },
      
      canSendMessage: () => {
        const { balance } = get()
        return balance.current > 0
      },

      // API Actions (dummy implementations)
      fetchBalance: async () => {
        set({ isLoading: true })
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000))
          // In real implementation, this would fetch from API
          set({ isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch balance',
            isLoading: false 
          })
        }
      },

      fetchUsage: async () => {
        set({ isLoading: true })
        try {
          await new Promise(resolve => setTimeout(resolve, 1000))
          set({ isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch usage',
            isLoading: false 
          })
        }
      },

      fetchTransactions: async () => {
        set({ isLoading: true })
        try {
          await new Promise(resolve => setTimeout(resolve, 1000))
          set({ isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch transactions',
            isLoading: false 
          })
        }
      },

      purchaseCredits: async (planId: string) => {
        set({ isLoading: true })
        try {
          await new Promise(resolve => setTimeout(resolve, 2000))
          const plan = get().availablePlans.find(p => p.id === planId)
          if (plan) {
            const newTransaction: CreditTransaction = {
              id: Date.now().toString(),
              type: 'purchase',
              amount: plan.credits,
              description: `${plan.name} Purchase`,
              timestamp: new Date().toISOString()
            }
            
            set((state) => ({
              balance: {
                ...state.balance,
                current: state.balance.current + plan.credits,
                total: state.balance.total + plan.credits,
                lastUpdated: new Date().toISOString()
              },
              transactions: [newTransaction, ...state.transactions],
              isLoading: false
            }))
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to purchase credits',
            isLoading: false 
          })
        }
      }
    }),
    {
      name: 'credit-store',
      partialize: (state) => ({
        balance: state.balance,
        usage: state.usage,
        transactions: state.transactions
      })
    }
  )
)

// Convenience hooks
export const useCredits = () => {
  const store = useCreditStore()
  return {
    balance: store.balance,
    usage: store.usage,
    isLoading: store.isLoading,
    error: store.error,
    usagePercentage: store.getUsagePercentage(),
    remainingCredits: store.getRemainingCredits(),
    canSendMessage: store.canSendMessage()
  }
}

export const useCreditActions = () => {
  const store = useCreditStore()
  return {
    updateBalance: store.updateBalance,
    updateUsage: store.updateUsage,
    addTransaction: store.addTransaction,
    fetchBalance: store.fetchBalance,
    fetchUsage: store.fetchUsage,
    fetchTransactions: store.fetchTransactions,
    purchaseCredits: store.purchaseCredits
  }
}